import { useSession } from "./useSession";

/**
 * Returns true when the current session is allowed to make structural
 * changes ("schema-level" mutations) in features that distinguish
 * admin-only actions from end-customer actions — Data Sheets being the
 * first consumer. Mirrors the legacy portal's
 * <c>MENU.ADMIN.SIMPLEDB.EDIT</c> rule: only admins or managers
 * impersonating a customer may touch the schema; regular customer users
 * can still read and edit row-level data.
 *
 * We pivot on the presence of an "original kind" that differs from the
 * current kind — the same cue the top-bar uses to render the
 * impersonation banner. A mismatched <c>originalKind</c> therefore acts
 * as a proxy for "the current user was admin before impersonating".
 */
export function useCanEditSchema(): boolean {
  const { session } = useSession();
  if (!session?.isAuthenticated) return false;
  if (session.kind !== "Customer") return false;
  // A restricted impersonation (e.g. BasicMenu for @fonix.com managers)
  // gets the customer's read/edit-rows view, never the schema editor.
  if (session.restriction) return false;
  const original = session.originalKind ?? null;
  return original === "Admin" || original === "Manager";
}
